import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import {
  complaints,
  type Complaint,
  type ComplaintCreate,
} from "@/api/complaints";
import { useToast } from "@/components/Toast";
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  FormError,
  Input,
  Label,
  Select,
} from "@/components/ui";

export function ComplaintsPage() {
  const list = useQuery({
    queryKey: ["my-complaints"],
    queryFn: () => complaints.list(),
  });
  const [openForm, setOpenForm] = useState(false);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-base font-semibold text-term-fg">Complaints</h1>
          <p className="text-2xs text-term-mute">
            File a formal complaint against the broker. We acknowledge
            within 1 business day; the regulatory clock starts from that
            acknowledgement.
          </p>
        </div>
        <Button onClick={() => setOpenForm((x) => !x)}>
          {openForm ? "Hide form" : "New complaint"}
        </Button>
      </div>

      {openForm && <NewComplaintForm onClose={() => setOpenForm(false)} />}

      <Card>
        <CardBody className="p-0">
          {list.isLoading ? (
            <p className="p-3 text-sm text-term-mute">Loading…</p>
          ) : !list.data || list.data.length === 0 ? (
            <EmptyState
              title="No complaints filed"
              description="If you have a problem you'd like a formal review of, click 'New complaint'. Casual support questions go through chat — this form is for formal cases that need an SLA."
            />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Filed</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Type</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Subject</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Status</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">SLA due</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((c: Complaint) => (
                  <tr key={c.id} className="border-t border-term-border">
                    <td className="px-3 py-1.5 text-term-dim">
                      {new Date(c.opened_at).toLocaleDateString()}
                    </td>
                    <td className="px-3 py-1.5">
                      <Badge>{c.classification}</Badge>
                    </td>
                    <td className="px-3 py-1.5 text-term-fg">{c.subject}</td>
                    <td className="px-3 py-1.5">{statusBadge(c.status)}</td>
                    <td className="px-3 py-1.5 text-term-dim">
                      {new Date(c.sla_due_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function NewComplaintForm({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState<ComplaintCreate>({
    classification: "execution",
    subject: "",
    body: "",
  });
  const [err, setErr] = useState<string | null>(null);
  const submit = useMutation({
    mutationFn: () => complaints.open(form),
    onSuccess: () => {
      toast.push("Complaint filed", "success");
      qc.invalidateQueries({ queryKey: ["my-complaints"] });
      onClose();
    },
    onError: (e) =>
      setErr(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });

  return (
    <Card>
      <CardBody>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setErr(null);
            submit.mutate();
          }}
          className="space-y-3"
        >
          <div>
            <Label>Type</Label>
            <Select
              value={form.classification}
              onChange={(e) => setForm({ ...form, classification: e.target.value })}
            >
              <option value="execution">execution — bad fill / slippage</option>
              <option value="billing">billing — withdrawal delay / fees</option>
              <option value="platform">platform — outage / UI bug</option>
              <option value="kyc">kyc — verification issue</option>
              <option value="abuse">abuse — harassment / hijack</option>
              <option value="other">other</option>
            </Select>
          </div>
          <div>
            <Label>Subject</Label>
            <Input
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              required
              minLength={3}
              maxLength={200}
            />
          </div>
          <div>
            <Label>Detail</Label>
            <textarea
              className="block h-32 w-full rounded border border-slate-300 dark:border-slate-700 bg-transparent p-2 text-xs"
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              required
              minLength={3}
              maxLength={10000}
            />
          </div>
          <FormError>{err}</FormError>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={submit.isPending}>
              File complaint
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}

function statusBadge(s: Complaint["status"]) {
  const tone =
    s === "resolved"
      ? "good"
      : s === "rejected"
      ? "bad"
      : s === "under_review"
      ? "warn"
      : "neutral";
  return <Badge tone={tone as "good" | "bad" | "warn" | "neutral"}>{s}</Badge>;
}
